import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Slide,
  alpha,
  Portal,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  OpenInFull as OpenInFullIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import ClientDetailsPage from '../../pages/ClientDetailsPage';

const MINIMIZED_BAR_HEIGHT = 56;

export interface ClientProfileModalInstance {
  instanceId: string;
  clientId: number;
  clientName?: string;
  minimized: boolean;
}

interface ClientDetailsModalProps {
  clientId: number;
  clientName?: string;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  onClose: () => void;
  stackIndex?: number;
  zIndex?: number;
}

interface ModalTitleBarProps {
  title: string;
  minimized: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
  onBarClick?: () => void;
}

const ModalTitleBar: React.FC<ModalTitleBarProps> = ({
  title,
  minimized,
  onMinimize,
  onRestore,
  onClose,
  onBarClick,
}) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onBarClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: minimized ? 0.5 : 0.75,
        minHeight: 48,
        flexShrink: 0,
        bgcolor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.15)
          : alpha(theme.palette.primary.main, 0.08),
        borderBottom: minimized ? 'none' : `1px solid ${theme.palette.divider}`,
        cursor: minimized ? 'pointer' : 'default',
        userSelect: 'none',
      }}
    >
      <PersonIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
      <Typography
        variant="body2"
        fontWeight={600}
        noWrap
        sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}
        title={title}
      >
        {title}
      </Typography>

      <Box
        display="flex"
        alignItems="center"
        onClick={(e) => e.stopPropagation()}
        sx={{ flexShrink: 0 }}
      >
        {minimized ? (
          <Tooltip title="Restaurar">
            <IconButton
              size="small"
              onClick={onRestore}
              sx={{ color: 'text.secondary' }}
            >
              <OpenInFullIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Minimizar">
            <IconButton
              size="small"
              onClick={onMinimize}
              sx={{ color: 'text.secondary' }}
            >
              <MinimizeIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Fechar">
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  clientId,
  clientName,
  minimized,
  onMinimizedChange,
  onClose,
  stackIndex = 0,
  zIndex,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const title = clientName
    ? `${clientId} • ${clientName}`
    : `Perfil do cliente #${clientId}`;

  const resolvedZIndex = zIndex ?? theme.zIndex.modal + stackIndex + 1;

  const embeddedTheme = useMemo(() => {
    const base = resolvedZIndex + 100;
    return createTheme(theme, {
      zIndex: {
        ...theme.zIndex,
        drawer: base,
        modal: base + 100,
        snackbar: base + 200,
        tooltip: base + 300,
      },
    });
  }, [theme, resolvedZIndex]);

  return (
    <>
      {minimized && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 16 + stackIndex * MINIMIZED_BAR_HEIGHT,
              right: 16,
              zIndex: resolvedZIndex,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.shadows[12],
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <ModalTitleBar
              title={title}
              minimized
              onMinimize={() => onMinimizedChange(true)}
              onRestore={() => onMinimizedChange(false)}
              onClose={onClose}
              onBarClick={() => onMinimizedChange(false)}
            />
          </Box>
        </Portal>
      )}

      <Dialog
        open={!minimized}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="xl"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={250}
        sx={{ zIndex: resolvedZIndex }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(4px)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            },
          },
        }}
        PaperProps={{
          sx: {
            margin: isMobile ? 0 : 1.5,
            maxHeight: isMobile ? '100vh' : '95vh',
            height: isMobile ? '100vh' : '95vh',
            borderRadius: isMobile ? 0 : 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <ModalTitleBar
          title={title}
          minimized={false}
          onMinimize={() => onMinimizedChange(true)}
          onRestore={() => onMinimizedChange(false)}
          onClose={onClose}
        />

        <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto' }}>
          <Box sx={{ minHeight: '100%' }}>
            <ThemeProvider theme={embeddedTheme}>
              <ClientDetailsPage
                clientId={clientId}
                embedded
                hideEmbeddedClose
                onClose={onClose}
              />
            </ThemeProvider>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientDetailsModal;
